/**
 * Part del State necessari pel mapa genèric
 * Component: MapaState.ts
 * Llenguatge: TypeScript
 * Autors: Xavier Naval
 * Versió: 1.0
 * Data: 26/04/2022
 * (c) Ajuntament de Manresa - abr 2022 - Departament de Sistemes d'Informació
 */

import { observable, action } from 'mobx';
import proj4 from 'proj4';
import OlLayerImage from 'ol/layer/Image';
import OlImageSource from 'ol/source/Image';
import OlLayerTile from 'ol/layer/Tile';
import OlTileSource from 'ol/source/Tile';
import OlSourceImageWMS from 'ol/source/ImageWMS';
import OlSourceTileWMS from 'ol/source/TileWMS';
import OlOverlay from 'ol/Overlay';
import OlView from 'ol/View';
import OlMap from 'ol/Map';
import OlScaleLine from 'ol/control/ScaleLine';
import { Options } from 'ol/source/WMTS';
import { Extent, getCenter } from 'ol/extent';
import { Coordinate } from 'ol/coordinate';
import { IPropsMapa } from '../interfaces/IPropsMapa';
import { IFinCod, IRefCad } from '../interfaces/IRefCadAdr';
import Util from '../config/util';
import BaseState from './BaseState';
import { IAttType, ILayer, ILayerGroup } from '../interfaces/ILayer';
// import Geolocation from 'ol/Geolocation';

// Zoom global mínim (lo més allunyat)
const minZ = 12;

// Zoom global màxim (lo més aprop)
const maxZ = 20;

export default class MapaState extends BaseState {
    // Constructor
    constructor(props: IPropsMapa, conf: string) {
        super(props, conf);
    }

    // Element HTML del contingut de l'overlay
    private popupContent: HTMLDivElement = document.createElement('div');

    // Element HTML de l'Overlay
    private popupElement: HTMLDivElement = this.createPopup(); // document.createElement('div');

    // El Overlay
    private popupOverlay = new OlOverlay({
        element: this.popupElement,
        autoPan: { animation: { duration: 250 } }
    });

    // Capa base inicial
    private iBase = '';

    // Capes inicialment activades
    private iLayers: string[];

    // Número d'adreça inicial
    private iNumCod = 0;

    // Referència cadastral inicial
    private iRefCad: string;

    // Codi de carrer inicial
    private iCarCod: string;

    // Tipus de via inicial
    private iTipCarr: string;

    // Nom de carrer inicial
    private iNomCarr: string;

    // Número postal inicial
    private iNumero: string;

    // Posició inicial
    private iPosX = 0
    private iPosY = 0
    private iPosZoom = 0
    private iPosPtX = 0;
    private iPosPtY = 0;
    private iLoc = false;

    // Parada de mercat incial
    private iPMerc: string;

    // BaseLayer actual
    @observable actBaseLayer: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;

    // Element del menu
    @observable anchorEl: HTMLButtonElement;

    // Crea el element del Popup
    private createPopup(): HTMLDivElement {
        const ppp: HTMLDivElement = document.createElement('div');
        ppp.className = 'guia-popup';
        ppp.id = 'popup';
        const pppclsr:HTMLAnchorElement = document.createElement('a');
        pppclsr.className = 'guia-popup-closer';
        pppclsr.id = 'popupcloser';
        pppclsr.href = '#';
        pppclsr.onclick = () => {
            this.popupOverlay.setPosition(undefined);
            document.getElementById('popupcloser').blur();
            return false;
        };
        ppp.appendChild(pppclsr);
        this.popupContent.id = 'popupcontent';
        this.popupContent.className = 'guia-popup-content';
        ppp.appendChild(this.popupContent);
        return ppp;
    }

    // Torna el zoom inicial
    private getInitialZoom() : number {
        if (this.mapConfig.initialZoom) {
            return this.mapConfig.initialZoom;
        } else{
            return this.getMinZoom(null);
        }
    }

    // Torna el zoom mínim
    private getMinZoom(ly: ILayer) : number {
        let mz: number = minZ;
        if (this.mapConfig.minZoom) {
            mz = this.mapConfig.minZoom;
        }
        if (ly && ly.minZoom) {
            mz = Math.max(mz, ly.minZoom);
        }
        return mz;
    }

    // Torna el zoom màxim
    private getMaxZoom(ly: ILayer) : number {
        let mz: number = maxZ;
        if (this.mapConfig.maxZoom) {
            mz = this.mapConfig.maxZoom;
        }
        if (ly && ly.maxZoom) {
            mz = Math.min(mz, ly.maxZoom);
        }
        return mz;
    }

    // Diu si una capa és una capa inicial
    private isILayer(ly: string): boolean {
        if (this.iLayers && this.iLayers.length > 0) {
            const lly = ly.toLowerCase();
            if (this.iLayers.filter(item => {
                const litem = item.toLowerCase();
                return lly === litem;
            }).length > 0) {
                return true;
            }
        }
        return false;
    }

    // Afegeix una capa de referencia
    // Si retorn 0 és que la capa afegida està desactivada
    // Si retorna 1 és que esta activada però no es pot fer una identificació sobre els elements
    // Si retorna 2 és que està activada i es poden identificar els elements
    private addRefLayer(rl: ILayer | ILayerGroup): number {
        let olLy: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
        if (rl.type === 'wms') {
            const rll = rl as ILayer;
            const visible = this.isILayer(rll.layers) || this.isILayer(rll.id);
            olLy = this.createWMSLayer(rll.id, rll.layers, rll.url, this.getLayerExtraParms(rll), rll.layers, rll.singleTile, true, visible, rll.opacity, rll.info, this.getMinZoom(rll), this.getMaxZoom(rll));
            this.refLayers.push(olLy);
            this.actRefLayers.push(visible);
            if (visible) {
                if (rl.info) {
                    return 2;
                }
                return 1;
            } else {
                return 0;
            }
        } else if (rl.type === 'wmts') {
            const rll = rl as ILayer;
            if (rll.cap) {
                const visible = this.isILayer(rll.layers) || this.isILayer(rll.id);
                olLy = this.createWMTSLayer(rll.id, rll.name, rll.cap as Options, visible, rll.opacity, this.getMinZoom(rll), this.getMaxZoom(rll));
                this.refLayers.push(olLy);
                this.actRefLayers.push(visible);
                if (visible) {
                    return 1;
                } else {
                    return 0;
                }
            }
            return 0;
        } else if (rl.type === 'grp') { // Capes de referència dins d'un grup
            let ret = 0;
            (rl as ILayerGroup).layers.forEach((rl2) => { ret = this.addRefLayer(rl2) || ret; });
            return ret;
        }
        return 0;
    }

    // Processem els paràmetres de la URL
    private getURLParameters() {
        const usp: URLSearchParams = new URLSearchParams(window.location.search);
        // Mirem si ens han passat Base
        this.iBase = usp.get('Base');
        // Mirem si ens han passat Layers
        const lys = usp.get('Layers');
        if (lys) {
            this.iLayers = usp.get('Layers').split(',');
        }
        // Mirem si ens han passat un NumCod
        const nc = usp.get('NumCod');
        if (nc) {
            this.iNumCod = parseInt(nc);
        }
        // Mirem si ens han passat una referència cadastral
        this.iRefCad = usp.get('RefCad');
        if (!this.iRefCad) {
            this.iRefCad = usp.get('RefPar');
            if (!this.iRefCad) {
                this.iRefCad = usp.get('Ref1') + usp.get('Ref2');
            }
        }
        // Eliminem les referècies de rústica de 7 dígits o les refrències de menys de 7 dígits
        if (this.iRefCad && (this.iRefCad === '08112A0' || this.iRefCad.length < 7)) {
            this.iRefCad = '';
        }
        // Mirem si ens han passat una adreça
        this.iCarCod = usp.get('CarCod');
        this.iTipCarr = usp.get('TipCarr');
        this.iNomCarr = usp.get('NomCarr');
        if (this.iNomCarr) {
            this.iNomCarr = this.iNomCarr.replace(/_/g, ' ');
        }
        this.iNumero = usp.get('Numero');
        // Mirem si ens han passat una posició i nivell de zoom
        const iPos: string = usp.get('Pos');
        if (iPos) {
            const iposA: Array<string> = iPos.split(';');
            if (iposA.length >= 2) {
                this.iPosX = parseFloat(iposA[0]);
                this.iPosY = parseFloat(iposA[1]);
                if (iposA.length > 2) {
                    this.iPosZoom = parseInt(iposA[2]);
                    if (iposA.length > 4) {
                        this.iPosPtX = parseFloat(iposA[3]);
                        this.iPosPtY = parseFloat(iposA[4]);
                    }
                }
            }
        }
        // Mirem si ens han passat una parada d emercat
        this.iPMerc = usp.get('Parada');
        // Mirem si volem la posició actual
        const iLoc: string = usp.get('Loc');
        if (iLoc) {
            this.iLoc = true;
        }
    }

    // Llegeix els atributs del Layer i mostra el Popup
    private getLayerAtts(c: Coordinate, content: string, layer: ILayer) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        layer.atts = [];
        fetch(layer.url + '?REQUEST=DescribeLayerType&VERSION=1.1.1&SERVICE=WMS&QUERY_LAYERS=' + layer.layers)
        .then(function (response: Response): Promise<string> {
            return response.text();
        })
        .then(function (text: string) {
            const pos = { pos: 0 };
            let ct: string = Util.getBetweenI('<element', '/>', text, pos);
            while (ct) {
                const iname: string = Util.getBetweenI('name="', '"', ct); // Nom de l'atribut
                if (iname) {
                    const idescription: string = Util.getBetweenI('description="', '"', ct) || iname;  // Descripció o títol de l'atribut
                    const idisplayOrder: number = parseInt(Util.getBetweenI('displayOrder="', '"', ct) || '0'); // Ordre de visualtzació a la pantalla
                    const itype: string = Util.getBetweenI('type="', '"', ct); // Tipus de dades boolean,blob,dateTime,gml:...,integer,unsignedInt,long,unsignedLong,short,unsignedShort,byte,unsignedByte,double,float,decimal
                    const itotalDigits: number = parseInt(Util.getBetweenI('totalDigits="', '"', ct) || '0'); // Número tolal de dígits pels tipus integer,unsignedInt,long,unsignedLong,short,unsignedShort,byte,unsignedByte,double,float,decimal,string
                    const ifractionDigits: number = parseInt(Util.getBetweenI('fractionDigits="', '"', ct) || '0'); // Número de dígits decimals pels tipus double,float,decimal
                    const imaxLength: number = parseInt(Util.getBetweenI('maxLength="', '"', ct) || '0'); // Lóngitud màxima pel tipus string
                    layer.atts.push({name: iname, description: idescription, displayOrder: idisplayOrder, type: itype, totalDigits: itotalDigits, fractionDigits: ifractionDigits, maxLength: imaxLength}); 
                }
                ct = Util.getBetweenI('<element', '/>', text, pos);
            }
            if (layer.atts.length >0) {
                that.setPoupContent2(c, content, layer.atts);
            } else {
                that.setPoupContent(c, content, undefined);
            }
        })
        .catch(function () {
            that.setPoupContent(c, content, undefined);
        });
    }

    // Posa el contingut del Popup d'informació fent servir els LayerAtts
    private setPoupContent2(c: Coordinate, content: string, atts: IAttType[]) {
        // Construim el títol
        const txHtmlTit = '<b>Informació</b><hr/>';
        // Construim el cos
        let txHtml = '';
        let txt = '';
        let numCod: string = undefined;
        let numDesc = '';
        let dt: number;
        for (let i = 0; i < atts.length; i++) {
            txt = Util.getBetweenI('<' + atts[i].name + '>', '</' + atts[i].name + '>', content);
            if (txt) {
                switch (atts[i].name.toUpperCase()) {
                    case 'IMGURL':
                        txHtml += '<a href="' + txt + '" target="_blank"><img src="' + txt + '" style="float:right; max-width:150px; max-height:150px; margin:5px"/></a>';
                        break;
                    case 'URL':
                        txHtml += atts[i].description + ': ' + '<a href="#" onclick="window.open(\'' + txt + '\')"><span style="color: blue; text-decoration:underline">' + txt + '</span></a><br />';
                        break;
                    case 'MAIL':
                        txHtml += atts[i].description + ': ' + '<a href="mailto:' + txt + '"><span style="color: blue; text-decoration:underline">' + txt + '</span></a><br />';
                        break;
                    case 'TELEFON':
                        txHtml += atts[i].description + ': ' + '<a href="tel:' + txt + '"><span style="color: blue; text-decoration:underline">' + txt + '</span></a><br />';
                        break;
                    case 'INFO':
                        txHtml += atts[i].description + ': ' + txt + '<br />';
                        break;
                    case 'HTMLINFO':
                        txHtml += atts[i].description + ': ' + Util.unescapeXML(txt) + '<br />';
                        break;
                    case 'NUMCOD':
                    case 'E.NUMCOD':
                        if (this.mapConfig.adrLayer) {
                            numCod = txt;
                            numDesc = atts[i].description;
                        } else {
                            txHtml += atts[i].description + ': ' + txt + '<br />';    
                        }
                        break;
                    default:
                        // boolean,blob,dateTime,gml:...,integer,unsignedInt,long,unsignedLong,short,unsignedShort,byte,unsignedByte,double,float,decimal
                        switch (atts[i].type) {
                            case 'boolean':
                                txHtml += atts[i].description + ': ' + '<input type="checkbox" ' + (Util.parseBoolean(txt) ? 'checked' : '') + ' disabled />< br/>'
                                break;
                            case 'dateTime':
                                dt = Date.parse(txt);
                                if (isNaN(dt)) {
                                    txHtml += atts[i].description + ': ' + txt + '<br />';    
                                } else {
                                    txHtml += atts[i].description + ': ' + Util.formatDateTime(new Date(dt)) + '<br />';
                                }
                                break;
                            default:
                                txHtml += atts[i].description + ': ' + txt + '<br />';
                                break;
                        }
                        break;
                }
            }
        }
        // Mirem si hi ha adreça
        if (numCod) { // Busquem la descripció de l'adreça
            const defaultParameters = {
                SERVICE: 'WFS',
                VERSION: '1.1.0',
                REQUEST: 'GetFeature',
                TYPENAME: this.mapConfig.adrLayer.layers,
                OUTPUTFORMAT: 'GML2',
                SRSNAME: 'EPSG:25831',
                FEATUREID: 'adr.' + numCod
                };
                this.req.getRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then((resultat) => {
                    const text_adr: string = Util.getBetweenI('ADRDESC>','<', resultat as string,{pos:0 });
                    if (text_adr) {
                        txHtml = numDesc + ': <b>' + text_adr + '</b><br />' + txHtml;
                    }
                    this.popupContent.innerHTML = txHtmlTit + txHtml;
                    this.popupOverlay.setPosition(c);
                });
        }
        else {
            this.popupContent.innerHTML = txHtmlTit + txHtml;
            this.popupOverlay.setPosition(c);
        }
    } 

    // Posa el contingut del Popup d'informació d'un element
    // Aquesta funciona com la de la guia
    private setPoupContent(c: Coordinate, content: string, layer: string) {
        if (this.mapConfig.infoType === 1) {
            const il: ILayer = this.getILayer(this.mapConfig.refLayers, layer);
            if (il) {
                if (il.atts) {
                    if (il.atts.length > 0) { // ja hem llegit els atributs
                        this.setPoupContent2(c, content, il.atts);
                        return;
                    }
                } else {
                    this.getLayerAtts(c, content, il); // provem de llegir els atributs (i ensenyem el popup)
                    return;
                }
            }
        }
        // content = content.replace(/\s+/g, ' ').trim(); // Traiem els retorns de carro, tabuladors, ...
        // Construim el títol
        let tit: string = Util.getBetweenI('<NAME>', '</NAME>', content); // Mirem si hi ha nom (versió nova de paràmetres)
        const txHtmlTit = '<b>' + (tit ? tit : 'Informació') + '</b><hr/>';
        // Construim el cos
        let txHtml = '';
        let txt = '';
        if (!tit) { // Mirem si és una parada
            txt = Util.getBetweenI('<NOM>', '</NOM>', content);
            if (txt) {
                txHtml += txt + '<br />';
                tit = '*'; // Per que vagi per la versió nova
            }
            txt = Util.getBetweenI('<PRODUCTE>', '</PRODUCTE>', content);
            if (txt) {
                txHtml += txt + '<br />';
                tit = '*'; // Per que vagi per la versió nova
            }
            txt = Util.getBetweenI('<NUMERO>', '</NUMERO>', content);
            if (txt) {
                txHtml += txt + '<br />';
                tit = '*'; // Per que vagi per la versió nova
            }
        }
        if (!tit) { // Versió vella
            txt = Util.getBetweenI('<DESCRIP>', '</DESCRIP>', content);
            if (!txt) {
                txt = Util.getBetweenI('<CAUSA>', '</CAUSA>', content);
            }
            if (txt) {
                txHtml += txt + '<br />';
            }
            txt = Util.getBetweenI('<OBSERV>', '</OBSERV>', content);
            if (txt) {
                txHtml += txt + '<br />';
            }
            // Dades dels parquings
            txt = Util.getBetweenI('<STATUS>', '</STATUS>', content);
            if (txt === 'LLIURE') {
                txt = Util.getBetweenI('<PLTOT>', '</PLTOT>', content);
                if (txt) {
                    const pltot = parseInt(txt);
                    if (pltot > 0) {
                        txt = Util.getBetweenI('<PLOCU>', '</PLOCU>', content);
                        if (txt) {
                            const plocu = parseInt(txt);
                           if (plocu < pltot) {
                                txHtml += (pltot - plocu).toString() + ' places lliures de ' + pltot.toString() + '<br />';
                            }
                        }
                    }
                }
            } else if (txt === 'PLE') {
                txHtml += 'Sense places lliures<br />';
            } else if (txt === 'TANCAT') {
                txHtml += 'Tancat<br />';
            }
            // Dades del codis postals
            txt = Util.getBetweenI('<CODPOS>', '</CODPOS>', content);
            if (txt) {
                txHtml += 'Codi postal: ' + txt + '<br />';
            }
        } else { // Versió nova
            txt = Util.getBetweenI('<IMGURL>', '</IMGURL>', content);
            if (txt) {
                txHtml += '<a href="' + txt + '" target="_blank"><img src="' + txt + '" style="float:right; max-width:150px; max-height:150px; margin:5px"/></a>';
            }
            txt = Util.getBetweenI('<DESCRIP>', '</DESCRIP>', content);
            if (txt) {
                txHtml += txt + '<br />';
            }
            txHtml += '<br />';
            txt = Util.getBetweenI('<URL>', '</URL>', content);
            if (txt) {
                txHtml += '<a href="#" onclick="window.open(\'' + txt + '\')"><span style="color: blue; text-decoration:underline">' + txt + '</span></a><br />';
            }
            txt = Util.getBetweenI('<MAIL>', '</MAIL>', content);
            if (txt) {
                txHtml += '<a href="mailto:' + txt + '"><span style="color: blue; text-decoration:underline">' + txt + '</span></a><br />';
            }
            txt = Util.getBetweenI('<TELEFON>', '</TELEFON>', content);
            if (txt) {
                txHtml += '<a href="tel:' + txt + '"><span style="color: blue; text-decoration:underline">' + txt + '</span></a><br />';
            }
            txt = Util.getBetweenI('<INFO>', '</INFO>', content);
            if (txt)
            {
                txHtml += '<br />' + txt + '<br />';
            }
            txt = Util.getBetweenI('<HTMLINFO>', '</HTMLINFO>', content);
            if (txt)
            {
                txHtml += Util.unescapeXML(txt) + '<br />';
            }
            // Dades dels parquings
            if (txt === 'LLIURE') {
                txt = Util.getBetweenI('<PLTOT>', '</PLTOT>', content);
                if (txt) {
                    const pltot = parseInt(txt);
                    if (pltot > 0) {
                        txt = Util.getBetweenI('<PLOCU>', '</PLOCU>', content);
                        if (txt) {
                            const plocu = parseInt(txt);
                            if (plocu < pltot) {
                                txHtml += (pltot - plocu).toString() + ' places lliures de ' + pltot.toString() + '<br />';
                            }
                        }
                    }
                }
            } else if (txt === 'PLE') {
                txHtml += 'Sense places lliures<br />';
            } else if (txt === 'TANCAT') {
                txHtml += 'Tancat<br />';
            }
        }
        // Mirem si hi ha adreça
        const num = Util.getBetweenI('<NUMCOD>', '</NUMCOD>', content) || Util.getBetweenI('<E.NUMCOD>', '</E.NUMCOD>', content);
        if (this.mapConfig.adrLayer && num) { // Busquem la descripció de l'adreça
            const defaultParameters = {
                SERVICE: 'WFS',
                VERSION: '1.1.0',
                REQUEST: 'GetFeature',
                TYPENAME: this.mapConfig.adrLayer.layers,
                OUTPUTFORMAT: 'GML2',
                SRSNAME: 'EPSG:25831',
                FEATUREID: 'adr.' + num.toString()
                };
                this.req.getRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then((resultat) => {
                    const text_adr: string = Util.getBetweenI('ADRDESC>','<', resultat as string,{pos:0 });
                    if (text_adr) {
                        txHtml = '<b>' + text_adr + '</b><br /><br />' + txHtml;
                    }
                    this.popupContent.innerHTML = txHtmlTit + txHtml;
                    this.popupOverlay.setPosition(c);
                });
        }
        else {
            this.popupContent.innerHTML = txHtmlTit + txHtml;
            this.popupOverlay.setPosition(c);
        }
    } 

    // Busca una posició per nom de carrer
    private setFIDFromCarrer = (carCod: string, carSig: string, carDesc: string, numNum: string) => {
        const xml = '<?xml version="1.0" encoding="utf-8"?><soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"><soap:Body><GetAdrecaList xmlns="http://www.ajmanresa.cat/GuiaWS"><pXML>' + 
                    Util.encodeHTML('<WSParam><Session>' + this.ses + '</Session><Usuari>' + this.usr + '</Usuari>' + 
                                    (carCod ? '<CarCod>' + carCod +  '</CarCod>' 
                                            : (carSig ? '<CarSig>' + carSig + '</CarSig>' : '') + '<CarDesc>' + carDesc + '</CarDesc>') + 
                                    '<NumNum>' + numNum + '</NumNum></WSParam>') + '</pXML></GetAdrecaList></soap:Body></soap:Envelope>';
        this.req.postRequestRaw(this.mapConfig.wsGuia, xml, { headers: {'Content-Type': 'text/xml; charset=utf-8'} }).then((result) => {
            const adrList: string = Util.getBetween("<GetAdrecaListResult>", "</", result as string);
            // Retorna el següent : "Adreca 1#403557.63#4621691.05#3275;Adreca 2#402546.23#4620345.09#1843"
            if (adrList) {
                const adrArray: string[] = adrList.split(";"); // Separa la cadena per ;
                if (adrArray.length > 0) {
                    const adreca: string[] = adrArray[0].split("#"); // Seapar la cadena per #
                    this.parceFid = '';
                    this.rparceFid = '';
                    this.numCod = parseInt(adreca[3]);
                    this.finCod = 0;
                    this.finRefCad1 = '';
                    this.finRefCad2 = '';
                            this.setZoomPosCoord([parseFloat(adreca[1]), parseFloat(adreca[2])], true);
                    if (this.parceLayer) {
                        this.setSelect(this.parceLayer, '');
                    }
                    if (this.rparceLayer) {
                        this.setSelect(this.rparceLayer, '');
                    }
                }
            }
        });                              
    }

    // Posa el zoom de la posició passada per paràmetre (zoom entre 14 i 20)
    private setInitialPos(posX: number, posY: number, posZoom: number, posPtX: number, posPtY: number) {
        const ext: Extent = [posX - 1, posY - 1, posX + 1, posY + 1];
        this.olview.fit(ext);
        if (posZoom >= 14 && posZoom <= 20) {
            this.olview.setZoom(posZoom);
        }
        if (posPtX && posPtY) {
            this.position = [posPtX, posPtY];
            this.setMarker();
        }
    }
    
    // Posa la posició del dispositiu
    public setActualLocation() {
        navigator.geolocation.getCurrentPosition((position) => {
            if (position) {
                // alert("Lon:" + position.coords.longitude + " Lat:" + position.coords.latitude);
                const lonLat = [position.coords.longitude, position.coords.latitude];
                const coord = proj4('EPSG:4326', this.olproj.getCode()).forward(lonLat);
                // alert("X:" + coord[0] + " Y:" + coord[1]);
                const ext: Extent = [coord[0] - 1, coord[1] - 1, coord[0] + 1, coord[1] + 1];
                this.olview.fit(ext);
                this.position = coord;
                this.setMarker();
            }
        });
        // const geolocation = new OlGeolocation({
        //     projection: this.olview.getProjection()
        // });
        // // geolocation.on('change:position', () => {
        // //     const coord = geolocation.getPosition();
        // //     if (coord) {
        // //         const ext: Extent = [coord[0] - 1, coord[1] - 1, coord[0] + 1, coord[1] + 1];
        // //         this.olview.fit(ext);
        // //         this.position = coord;
        // //         this.setMarker();
        // //     }
        // // });
        // const coord: Coordinate = geolocation.getPosition();
        // if (coord) {
        //     const ext: Extent = [coord[0] - 1, coord[1] - 1, coord[0] + 1, coord[1] + 1];
        //     this.olview.fit(ext);
        //     this.position = coord;
        //     this.setMarker();
        // }
    }

    // Construim el mapa i hi afegim la llista de capes i el controls estàndard
    // Tal com funciona ara el control MyLayerSwitcher, només afegim al mapa una capa base
    public createMap() {
        this.getURLParameters(); // llegim els paràmetres de la URL
        let showNoms = false;
        this.mapConfig.baseLayers.forEach((bl) => { // Capes base
            const act: boolean = this.iBase && (bl.id.toLowerCase() === this.iBase.toLowerCase() || bl.layers.toLowerCase() === this.iBase.toLowerCase());
            let olLy: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
            if (bl.type === 'wms') {
                olLy = this.createWMSLayer(bl.id, bl.layers, bl.url, this.getLayerExtraParms(bl), bl.layers, bl.singleTile, true, act, 1.0, false, this.getMinZoom(bl), this.getMaxZoom(bl)); // Posem com a visible la passada per paràmetres
                this.baseLayers.push(olLy);
            } else if (bl.type === 'wmts' && bl.cap) {
                olLy = this.createWMTSLayer(bl.id, bl.name, bl.cap as Options, act, 1.0, this.getMinZoom(bl), this.getMaxZoom(bl)); // Posem com a visible la 1a capa base
                this.baseLayers.push(olLy);
            }
            if (act) {
                showNoms = bl.noms;
                this.actBaseLayer = olLy;
            }
        });
        // NO hem trobat la capa base
        if (!this.actBaseLayer) {
            showNoms = this.mapConfig.baseLayers[0].noms;
            this.baseLayers[0].setVisible(true);
            this.actBaseLayer = this.baseLayers[0];
        }
        this.olview = new OlView({
            projection: this.olproj, // Projecció ETRS89 UTM 31N
            center: getCenter(this.mapConfig.initialExtent),
            extent: Util.isEmptyExtent(this.mapConfig.maxExtent) ? this.mapConfig.initialExtent : this.mapConfig.maxExtent,
            zoom: this.getInitialZoom(),
            minZoom: this.getMinZoom(null),
            maxZoom: this.getMaxZoom(null)
        });
        let actRef = 0;
        this.mapConfig.refLayers.forEach((rl) => { // Capes de referència
            actRef = this.addRefLayer(rl) || actRef;
        });

        // Tal com funciona ara el control MyLayerSwitcher, només afegim al mapa una capa base
        this.olmap = new OlMap({
            layers: [this.actBaseLayer].concat(this.refLayers), // this.baseLayers.concat(this.refLayers),
            view: this.olview
        });
        this.olmap.on('postcompose', this.olmap.updateSize);
        this.olmap.on('singleclick', this.processMapClick); // Funció per identificar un element
        if (this.mapConfig.measureTool) {
            this.olmap.on('pointermove', this.pointerMove);
        }

        // Per cancel·lar les comandes amb el botó dret.
        this.olmap.getViewport().addEventListener('contextmenu', this.cancelMapInteraction);

        if (this.mapConfig.scaleBar) { // Afegim el control escala si cal
            this.olmap.addControl(new OlScaleLine({
                units: 'metric',
                bar: false,
                steps: 4,
                text: true,
                minWidth: 140
            }));
        }
        const ctrls = this.olmap.getControls();
        // Sembla que el Duration fa que no funcioni
        ctrls.forEach(element => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (element as any).duration_ = 0;
        });
        const intrs = this.olmap.getInteractions();
        intrs.forEach(element => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (element as any).duration_ = 0;
        });

        // Afegim les capes de parcel·les
        if (this.mapConfig.parceLayer && this.mapConfig.parceLayer.type === 'wms') {
            // this.parceLayer = this.createWMSLayer(this.mapConfig.parceLayer.id, this.mapConfig.parceLayer.name, this.mapConfig.parceLayer.url, { SELECT: this.mapConfig.parceLayer.layers + '.0' }, this.mapConfig.parceLayer.layers, true, true, true, 1.0, true, this.getMinZoom(this.mapConfig.parceLayer), this.getMaxZoom(this.mapConfig.parceLayer));
            this.parceLayer = this.createWMSLayer(this.mapConfig.parceLayer.id, this.mapConfig.parceLayer.name, this.mapConfig.parceLayer.url, { }, this.mapConfig.parceLayer.layers, true, true, true, 1.0, true, this.getMinZoom(this.mapConfig.parceLayer), this.getMaxZoom(this.mapConfig.parceLayer));
            this.parceLayer.setOpacity(0.5);
            this.olmap.addLayer(this.parceLayer);
        }
        if (this.mapConfig.rparceLayer && this.mapConfig.rparceLayer.type === 'wms') {
            // this.rparceLayer = this.createWMSLayer(this.mapConfig.rparceLayer.id, this.mapConfig.rparceLayer.name, this.mapConfig.rparceLayer.url, { SELECT: this.mapConfig.rparceLayer.layers + '.0' }, this.mapConfig.rparceLayer.layers, true, true, true, 1.0, true, this.getMinZoom(this.mapConfig.rparceLayer), this.getMaxZoom(this.mapConfig.rparceLayer));
            this.rparceLayer = this.createWMSLayer(this.mapConfig.rparceLayer.id, this.mapConfig.rparceLayer.name, this.mapConfig.rparceLayer.url, { }, this.mapConfig.rparceLayer.layers, true, true, true, 1.0, true, this.getMinZoom(this.mapConfig.rparceLayer), this.getMaxZoom(this.mapConfig.rparceLayer));
            this.rparceLayer.setOpacity(0.5);
            this.olmap.addLayer(this.rparceLayer);
        }

        // Afegim la capa de noms
        if (this.mapConfig.nomsLayer) {
            if (this.mapConfig.nomsLayer.type === 'wms') {
                this.nomsLayer = this.createWMSLayer(this.mapConfig.nomsLayer.id, this.mapConfig.nomsLayer.name, this.mapConfig.nomsLayer.url, this.getLayerExtraParms(this.mapConfig.nomsLayer), this.mapConfig.nomsLayer.layers, true, true, showNoms, 1.0, false, this.getMinZoom(this.mapConfig.nomsLayer), this.getMaxZoom(this.mapConfig.nomsLayer));
                this.olmap.addLayer(this.nomsLayer);
            } else if (this.mapConfig.nomsLayer.type === 'wmts' && this.mapConfig.nomsLayer.cap) {
                this.nomsLayer = this.createWMTSLayer(this.mapConfig.nomsLayer.id, this.mapConfig.nomsLayer.name, this.mapConfig.nomsLayer.cap as Options, showNoms, 1.0, this.getMinZoom(this.mapConfig.nomsLayer), this.getMaxZoom(this.mapConfig.nomsLayer));
                this.olmap.addLayer(this.nomsLayer);
            }
            this.setShowNoms(!!this.nomsLayer && showNoms);
            this.setNomsVisible(!!this.nomsLayer && showNoms);
        }

        // Afegim les capa de selecció
        if (this.mapConfig.selLayer && this.mapConfig.selLayer.type === 'wms') {
            // this.parceLayer = this.createWMSLayer(this.mapConfig.selLayer.id, this.mapConfig.selLayer.name, this.mapConfig.selLayer.url, { SELECT: this.mapConfig.selLayer.layers + '.0' }, this.mapConfig.selLayer.layers, true, true, true, 1.0, true, this.getMinZoom(this.mapConfig.selLayer), this.getMaxZoom(this.mapConfig.selLayer));
            this.selLayer = this.createWMSLayer(this.mapConfig.selLayer.id, this.mapConfig.selLayer.name, this.mapConfig.selLayer.url, { }, this.mapConfig.selLayer.layers, true, true, true, 1.0, true, this.getMinZoom(this.mapConfig.selLayer), this.getMaxZoom(this.mapConfig.selLayer));
            this.selLayer.setOpacity(0.5);
            this.olmap.addLayer(this.selLayer);
        }

        // Afegim la capa de marcadors
        this.addMarkerLayer();

        // Creem el tooltip d'ajuda
        this.olmap.addOverlay(this.popupOverlay);

        // Mirem si volem l'event de quan el mapa s'ha carregat
        if (this.iNumCod > 0) {
            this.olmap.once('rendercomplete', () => this.derivedSetFIDFromNumCod(this.iNumCod));
        } else if (this.iRefCad) {
            this.olmap.once('rendercomplete', () => this.derivedSetFIDFromRefCad(this.iRefCad.substring(0, 7), this.iRefCad.substring(7, 14)));
        } else if (this.iCarCod || this.iNomCarr) {
            this.olmap.once('rendercomplete', () => this.setFIDFromCarrer(this.iCarCod, this.iTipCarr, this.iNomCarr, this.iNumero));
        } else if (this.iPMerc) {
            this.olmap.once('rendercomplete', () => this.setFIDFromPMercat(this.iPMerc));
        } else if (this.iPosX || this.iPosY) {
            this.olmap.once('rendercomplete', () => this.setInitialPos(this.iPosX, this.iPosY, this.iPosZoom, this.iPosPtX, this.iPosPtY));
        } else if (this.iLoc) {
            this.olmap.once('rendercomplete', () => this.setActualLocation());
        }

        if (actRef > 0) {
            this.olmap.getLayers().item(0).setOpacity(0.75);
            if (actRef > 1) {
                this.setIdentElement(true);
            }
        }

        // Creem el tooltip d'ajuda
        if (this.mapConfig.measureTool) {
            this.helpTooltipElement.className = 'ol-tooltip hidden';
            this.olmap.addOverlay(this.helpTooltip);
        }
    }

    // Busquem el FID d'una finca (a partir de la referència cadastral)
    public derivedSetFIDFromRefCad(finRefCad1: string, finRefCad2: string) {
        if (this.parceLayer && this.rparceLayer && finRefCad1 && finRefCad1.length === 7 && (!finRefCad2 || (finRefCad2 && finRefCad2.length === 7))) {
            const defaultParameters = {
            SERVICE: 'WFS',
            VERSION: '1.1.0',
            REQUEST: 'GetFeature',
            TYPENAME: this.mapConfig.parceLayer.layers,
            OUTPUTFORMAT: 'GML2',
            SRSNAME: 'EPSG:25831',
            FILTER: finRefCad2 ? '<Filter><And><PropertyIsEqualTo><PropertyName>FINREFCAD1</PropertyName><Literal>' + 
                    finRefCad1 + 
                    '</Literal></PropertyIsEqualTo><PropertyIsEqualTo><PropertyName>FINREFCAD2</PropertyName><Literal>' + 
                    finRefCad2 + 
                    '</Literal></PropertyIsEqualTo></And></Filter>' : '<Filter><PropertyIsEqualTo><PropertyName>FINREFCAD1</PropertyName><Literal>' + 
                    finRefCad1 + 
                    '</Literal></PropertyIsEqualTo></Filter>'
            };
            this.req.getRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then((resultat1) => {
                let newFID = Util.getBetween(this.mapConfig.parceLayer.layers + ' fid="', '">', resultat1 as string);
                if (newFID) {
                    this.refCadWebApi.getFinCod(finRefCad1 + finRefCad2).then((resultfc1: IFinCod) => {
                        this.numCod = 0;
                        this.finCod = resultfc1.FINCOD;
                        this.finRefCad1 = finRefCad1;
                        this.finRefCad2 = finRefCad2;
                        this.parceFid = newFID;
                        this.rparceFid = '';
                        this.setZoomPos(resultat1 as string, true);
                        this.setSelect(this.rparceLayer, '');
                        this.setSelect(this.parceLayer, newFID);
                    });
                } else {
                    defaultParameters.TYPENAME = this.mapConfig.rparceLayer.layers;
                    this.req.getRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then((resultat2) => {
                        newFID = Util.getBetween(this.mapConfig.rparceLayer.layers + ' fid="', '">', resultat2 as string);
                        if (newFID) {
                            this.refCadWebApi.getFinCod(finRefCad1 + finRefCad2).then((resultfc2: IFinCod) => {
                                this.numCod = 0;
                                this.finCod = resultfc2.FINCOD;
                                this.finRefCad1 = finRefCad1;
                                this.finRefCad2 = finRefCad2;
                                this.parceFid = '';
                                this.rparceFid = newFID;
                                this.setZoomPos(resultat2 as string, true);
                                this.setSelect(this.parceLayer, '');
                                this.setSelect(this.rparceLayer, newFID);
                            });
                        }
                    });
                }
            });
        }
    }

    // Busquem el FID d'una parada de mercat (a partir del clasnum del VNS.PAR)
    public setFIDFromPMercat(pMerc: string) {
        if (this.selLayer && this.selLayer.get('id') === 'pmercat' && pMerc) {
            const defaultParameters = {
            SERVICE: 'WFS',
            VERSION: '1.1.0',
            REQUEST: 'GetFeature',
            TYPENAME: this.mapConfig.selLayer.layers,
            OUTPUTFORMAT: 'GML2',
            SRSNAME: 'EPSG:25831',
            FILTER: '<Filter><And><PropertyIsEqualTo><PropertyName>CLASCOD</PropertyName><Literal>' + 
                    'VNS.PAR' + 
                    '</Literal></PropertyIsEqualTo><PropertyIsEqualTo><PropertyName>CLASNUM</PropertyName><Literal>' + 
                    pMerc + 
                    '</Literal></PropertyIsEqualTo></And></Filter>'
            };
            this.req.getRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then((resultat1) => {
                const newFID = Util.getBetween(this.mapConfig.selLayer.layers + ' fid="', '">', resultat1 as string);
                if (newFID) {
                    this.pParada = pMerc;
                    this.numCod = 0;
                    this.finCod = 0;
                    this.finRefCad1 = '';
                    this.finRefCad2 = '';
                    this.parceFid = '';
                    this.rparceFid = '';
                    this.setZoomPos(resultat1 as string, true);
                    this.setSelect(this.selLayer, newFID);
                }
            });
        }
    }

    // Busquem el FID d'una finca (a partir del FinCod)
    public derivedSetFIDFromFinCod(finCod: number) {
        if (this.parceLayer && this.rparceLayer && finCod) {
            this.refCadWebApi.getRefCad(finCod).then((resultrc: IRefCad) => {
                const finRefCad1: string = resultrc.FINREFCAD1;
                const finRefCad2: string = resultrc.FINREFCAD2;
                const defaultParameters = {
                    SERVICE: 'WFS',
                    VERSION: '1.1.0',
                    REQUEST: 'GetFeature',
                    TYPENAME: this.mapConfig.parceLayer.layers,
                    OUTPUTFORMAT: 'GML2',
                    SRSNAME: 'EPSG:25831',
                    FILTER: '<Filter><And><PropertyIsEqualTo><PropertyName>FINREFCAD1</PropertyName><Literal>' + 
                            finRefCad1 + 
                            '</Literal></PropertyIsEqualTo><PropertyIsEqualTo><PropertyName>FINREFCAD2</PropertyName><Literal>' + 
                            finRefCad2 + 
                            '</Literal></PropertyIsEqualTo></And></Filter>'
                };
                this.req.getRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then((resultat1) => {
                    let newFID = Util.getBetween(this.mapConfig.parceLayer.layers + ' fid="', '">', resultat1 as string);
                    if (newFID) {
                        this.numCod = 0;
                        this.finCod = finCod;
                        this.finRefCad1 = finRefCad1;
                        this.finRefCad2 = finRefCad2;
                        this.parceFid = newFID;
                        this.rparceFid = '';
                        this.pParada = '';
                        this.setZoomPos(resultat1 as string, true);
                        this.setSelect(this.rparceLayer, '');
                        this.setSelect(this.parceLayer, newFID);
                    } else {
                        defaultParameters.TYPENAME = this.mapConfig.rparceLayer.layers;
                        this.req.getRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then((resultat2) => {
                            newFID = Util.getBetween(this.mapConfig.rparceLayer.layers + ' fid="', '">', resultat2 as string);
                            if (newFID) {
                                this.numCod = 0;
                                this.finCod = finCod;
                                this.finRefCad1 = finRefCad1;
                                this.finRefCad2 = finRefCad2;
                                this.parceFid = '';
                                this.rparceFid = newFID;
                                this.pParada = '';
                                this.setZoomPos(resultat2 as string, true);
                                this.setSelect(this.parceLayer, '');
                                this.setSelect(this.rparceLayer, newFID);
                            }
                        });
                    }
                });
            });
        }
    }

    // Busquem el FID d'un Numero (a partir del NumCod)
    public derivedSetFIDFromNumCod(numCod: number) {
        if (this.mapConfig.adrLayer && numCod) {
            const defaultParameters = {
            SERVICE: 'WFS',
            VERSION: '1.1.0',
            REQUEST: 'GetFeature',
            TYPENAME: this.mapConfig.adrLayer.layers,
            OUTPUTFORMAT: 'GML2',
            SRSNAME: 'EPSG:25831',
            FEATUREID: 'adr.' + numCod.toString()
            };
            this.req.getRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then((resultat1) => {
                const newFID = Util.getBetween(this.mapConfig.adrLayer.layers + ' fid="', '">', resultat1 as string);
                if (newFID) {
                    this.parceFid = '';
                    this.rparceFid = '';
                    this.numCod = numCod;
                    this.finCod = 0;
                    this.finRefCad1 = '';
                    this.finRefCad2 = '';
                    this.pParada = '';
                    // this.fid = newFID;
                    this.setZoomPos(resultat1 as string, true);
                    if (this.parceLayer) {
                        this.setSelect(this.parceLayer, '');
                    }
                    if (this.rparceLayer) {
                        this.setSelect(this.rparceLayer, '');
                    }
                }
            });
        }
    }

    // Busquem el FID d'una selLayer (parada) adreça i/o d'una finca (a partir d'una coordenada)
    public derivedSetFIDFromPos = (c: Coordinate) => {
        if (this.selLayer) {
            const xml = '<?xml version="1.0" ?><GetFeature version="1.1.0" service="WFS" outputFormat="GML2" xmlns:ogc="http://www.opengis.net/ogc" xmlns:gml="http://www.opengis.net/gml"><Query typeName="' + 
                        this.mapConfig.selLayer.layers + '" srsName="EPSG:25831"><Filter><Intersects><PropertyName>GEOM</PropertyName><gml:Point gid="1" srsName="EPSG:25831"><gml:coordinates>' + 
                        c[0].toString() + ',' + c[1].toString() + '</gml:coordinates></gml:Point></Intersects></Filter></Query></GetFeature>';
            this.req.postRequestRaw(this.mapConfig.wfsUrl, xml, { headers: {'Content-Type': 'text/html;'} }).then((result) => {
                const newFID = Util.getBetween(this.mapConfig.selLayer.layers + ' fid="', '">', result as string);
                if (newFID) {
                    this.pParada = Util.getBetweenI('CLASNUM>', '<', result as string);
                    this.numCod = 0;
                    this.finCod = 0;
                    this.finRefCad1 = '';
                    this.finRefCad2 = '';
                    this.parceFid = '';
                    this.rparceFid = '';
                    this.setZoomPos(result as string, true);
                    this.setSelect(this.selLayer, newFID);
                } else {
                    this.derivedSetFIDFromPos2(c);
                }
            }).catch(() => { /* No fa res*/ });
        } else {
            this.derivedSetFIDFromPos2(c);
        }
    }
    
    // Busquem el FID d'una adreça i/o d'una finca (a partir d'una coordenada)
    // 10/04/2021 -> Al punxar una posició si està aprop d'una adreça, agafa la posició de l'adreça, però si esta dins d'una parcel·la
    //               ja no agafa la posició del centroide (com feia fins ara) si no que es queda amb la posició punxada
    //               (és a dir he canviat les crides a setZoomPos per crides a setZoomPosCoord quan troba una parcel·la)
    public derivedSetFIDFromPos2 = (c: Coordinate) => {
        const xml = '<?xml version="1.0" ?><GetFeature version="1.1.0" service="WFS" outputFormat="GML2" xmlns:ogc="http://www.opengis.net/ogc" xmlns:gml="http://www.opengis.net/gml"><Query typeName="' + 
                this.mapConfig.adrLayer.layers + '" srsName="EPSG:25831"><Filter><BBOX><PropertyName>GEOM</PropertyName><gml:Envelope><gml:lowerCorner>' + 
                (c[0] - BaseState.margin).toString() + ',' + (c[1] - BaseState.margin).toString() + '</gml:lowerCorner><gml:upperCorner>' + 
                (c[0] + BaseState.margin).toString() + ',' + (c[1] + BaseState.margin).toString() + '</gml:upperCorner></gml:Envelope></BBOX></Filter></Query></GetFeature>';
        this.req.postRequestRaw(this.mapConfig.wfsUrl, xml, { headers: {'Content-Type': 'text/html;'}}).then((result) => {
            let trobat = false;
            const newFID = Util.getBetween(this.mapConfig.adrLayer.layers + ' fid="', '">', result as string);
            if (newFID) {
                this.finCod = 0;
                this.finRefCad1 = '';
                this.finRefCad2 = '';
                this.pParada = '';
                this.numCod = parseInt(Util.getBetween('adr.', '.', newFID + '.'));
                this.setZoomPos(result as string, true);
                this.identPosition = 0;
                trobat = true;
            }
            const xml = '<?xml version="1.0" ?><GetFeature version="1.1.0" service="WFS" outputFormat="GML2" xmlns:ogc="http://www.opengis.net/ogc" xmlns:gml="http://www.opengis.net/gml"><Query typeName="' + 
                        this.mapConfig.parceLayer.layers + '" srsName="EPSG:25831"><Filter><Intersects><PropertyName>GEOM</PropertyName><gml:Point gid="1" srsName="EPSG:25831"><gml:coordinates>' + 
                        c[0].toString() + ',' + c[1].toString() + '</gml:coordinates></gml:Point></Intersects></Filter></Query></GetFeature>';
            this.req.postRequestRaw(this.mapConfig.wfsUrl, xml, { headers: {'Content-Type': 'text/html;'} }).then((result) => {
                const newFID = Util.getBetween(this.mapConfig.parceLayer.layers + ' fid="', '">', result as string);
                const finRefCad1 = Util.getBetweenI('FINREFCAD1>', '<', result as string);
                const finRefCad2 = Util.getBetweenI('FINREFCAD2>', '<', result as string);
                if (newFID && finRefCad1 && finRefCad2) {
                    this.refCadWebApi.getFinCod(finRefCad1 + finRefCad2).then((resultfc1: IFinCod) => {
                        this.parceFid = newFID;
                        this.rparceFid = '';
                        this.pParada = '';
                        this.setSelect(this.rparceLayer, '');
                        this.setSelect(this.parceLayer, newFID)
                        if (!trobat) {
                            this.numCod = 0;
                            this.finCod = resultfc1.FINCOD;
                            this.finRefCad1 = finRefCad1;
                            this.finRefCad2 = finRefCad2;
                            this.setZoomPosCoord(c, true);
                            // this.setZoomPos(result as string, true);
                            this.identPosition = 0;
                        }
                    }).catch(() => { /* No fa res*/ });
                } else {
                    const xml = '<?xml version="1.0" ?><GetFeature version="1.1.0" service="WFS" outputFormat="GML2" xmlns:ogc="http://www.opengis.net/ogc" xmlns:gml="http://www.opengis.net/gml"><Query typeName="' + 
                                this.mapConfig.rparceLayer.layers + '" srsName="EPSG:25831"><Filter><Intersects><PropertyName>GEOM</PropertyName><gml:Point gid="1" srsName="EPSG:25831"><gml:coordinates>' + 
                                c[0] + ',' + c[1] + '</gml:coordinates></gml:Point></Intersects></Filter></Query></GetFeature>';
                    this.req.postRequestRaw(this.mapConfig.wfsUrl, xml, { headers: {'Content-Type': 'text/html;'} }).then((result) => {
                        const newFID = Util.getBetween(this.mapConfig.rparceLayer.layers + ' fid="', '">', result as string);
                        const finRefCad1 = Util.getBetweenI('FINREFCAD1>', '<', result as string);
                        const finRefCad2 = Util.getBetweenI('FINREFCAD2>', '<', result as string);
                        if (newFID && finRefCad1 && finRefCad2) {
                            this.refCadWebApi.getFinCod(finRefCad1 + finRefCad2).then((resultfc1: IFinCod) => {
                                this.parceFid = '';
                                this.rparceFid = newFID;
                                this.pParada = '';
                                this.setSelect(this.parceLayer, '');
                                this.setSelect(this.rparceLayer, newFID)
                                if (!trobat) {
                                    this.numCod = 0;
                                    this.finCod = resultfc1.FINCOD;
                                    this.finRefCad1 = finRefCad1;
                                    this.finRefCad2 = finRefCad2;
                                    this.setZoomPosCoord(c, true);
                                    // this.setZoomPos(result as string, true);
                                    this.identPosition = 0;
                                }
                            }).catch(() => { /* No fa res*/ });
                        } else { // Guardem la posició punxida sense guardar adreça ni referència
                            this.parceFid = '';
                            this.rparceFid = '';
                            this.setSelect(this.parceLayer, '');
                            this.setSelect(this.rparceLayer, '');
                            this.finCod = 0;
                            this.finRefCad1 = '';
                            this.finRefCad2 = '';
                            this.pParada = '';
                            if (!trobat) {
                                this.numCod = 0;
                                this.setZoomPosCoord(c, true);
                                this.identPosition = 0;
                            }
                        }
                    }).catch(() => { /* No fa res*/ }); 
                }
            }).catch(() => { /* No fa res*/ }); 
        }).catch(() => { /* No fa res*/ });
    }

    // Llegim un element a partir d'una coordenada
    // iLy és el 1r layer on comencem a buscar
    public derivedGetElementFromPos = (c: Coordinate, iLy?: number) => {
        for (let i = iLy; i < this.actRefLayers.length; i++) {
            if (this.actRefLayers[i]) {
                const ly: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource> =  this.refLayers[i];
                if (ly.get('info')) { // Mirem que podem demanar informació
                    let src: OlSourceImageWMS | OlSourceTileWMS;
                    // Mirem que sigui un wms
                    if (ly.get('singleTile')) {
                        src = (ly as OlLayerImage<OlImageSource>).getSource() as OlSourceImageWMS;
                    } else if (!ly.get('cap')) {
                        src = (ly as OlLayerTile<OlTileSource>).getSource() as OlSourceTileWMS;
                    }
                    if (src) {
                        const lyn = src.getParams();
                        const url = src.getFeatureInfoUrl(c, this.olview.getResolution(), src.getProjection(), {'INFO_FORMAT': 'application/vnd.ogc.gml', 'QUERY_LAYERS': lyn.layers});
                        this.req.getRequest(url).then((result) => {
                            let content = '';
                            if (typeof result === 'string' || result instanceof String) {
                                content = Util.getBetween('<gml:featureMember>', '</gml:featureMember>', result as string);
                            } 
                            if (content) { // Mostrar el diàleg
                                this.setPoupContent(c, content, lyn.layers);
                            } else { // No hem trobat res sota del cursor. Mirem si hi ha més capes activades i hem de buscar una altra feature
                                this.derivedGetElementFromPos(c, i + 1);
                            }
                        }); 
                        break; // for
                    }
                }
            }
        }
    }

    // // Busquem el FID d'un element element a partir d'una coordenada si el trobem mostrem la foto, si no mostrem la foto de la façana
    // // iLy és el 1r layer on comencem a buscar
    // public derivedGetFIDFromPosPhotoElem = (c: Coordinate, iLy?: number): string => {
    //     for (let i = iLy; i < this.actRefLayers.length; i++) {
    //         if (this.actRefLayers[i]) {
    //             const ly: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource> =  this.refLayers[i];
    //             if (ly.get('info')) { // Mirem que podem demanar informació
    //                 let src: OlSourceImageWMS | OlSourceTileWMS;
    //                 // Mirem que sigui un wms
    //                 if (ly.get('singleTile')) {
    //                     src = (ly as OlLayerImage<OlImageSource>).getSource() as OlSourceImageWMS;
    //                 } else if (!ly.get('cap')) {
    //                     src = (ly as OlLayerTile<OlTileSource>).getSource() as OlSourceTileWMS;
    //                 }
    //                 if (src) {
    //                     const lyn = src.getParams();
    //                     const url = src.getFeatureInfoUrl(c, this.olview.getResolution(), src.getProjection(), {'INFO_FORMAT': 'application/vnd.ogc.gml', 'QUERY_LAYERS': lyn.layers});
    //                     this.req.getRequest(url).then((result) => {
    //                         let content = '';
    //                         if (typeof result === 'string' || result instanceof String) {
    //                             content = Util.getBetween('<gml:featureMember>', '</gml:featureMember>', result as string);
    //                         } 
    //                         if (content) { // Retornar el fid
    //                             return Util.getBetweenI(' fid="','"', content as string,{pos:0 });
    //                         } else { // No hem trobat res sota del cursor. Mirem si hi ha més capes activades i hem de buscar una altra feature
    //                             return this.derivedGetFIDFromPosPhotoElem(c, i + 1);
    //                         }
    //                     }); 
    //                     break; // for
    //                 }
    //             }
    //         }
    //     }
    //     return undefined;
    // }

    public async derivedGetFIDFromPosPhotoElem(c: Coordinate, iLy: number): Promise<string | undefined> {
        for (let i = iLy; i < this.actRefLayers.length; i++) {
            if (this.actRefLayers[i]) {
                const ly: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource> = this.refLayers[i];
                if (ly.get('info')) { // Check if we can request information
                    let src: OlSourceImageWMS | OlSourceTileWMS;
                    // Check if it's a WMS
                    if (ly.get('singleTile')) {
                        src = (ly as OlLayerImage<OlImageSource>).getSource() as OlSourceImageWMS;
                    } else if (!ly.get('cap')) {
                        src = (ly as OlLayerTile<OlTileSource>).getSource() as OlSourceTileWMS;
                    }
                    if (src) {
                        const params = src.getParams();
                        const url = src.getFeatureInfoUrl(
                            c,
                            this.olview.getResolution(),
                            src.getProjection(),
                            {
                                'INFO_FORMAT': 'application/vnd.ogc.gml',
                                'QUERY_LAYERS': params.layers
                            }
                        );
                        try {
                            const result = await this.req.getRequest(url);
                            let content = '';
                            if (typeof result === 'string' || result instanceof String) {
                                content = Util.getBetween('<gml:featureMember>', '</gml:featureMember>', result as string);
                            }
                            if (content) { // Return the FID
                                return Util.getBetweenI(' fid="', '"', content as string, { pos: 0 });
                            } else { // No feature found, check next layer
                                return await this.derivedGetFIDFromPosPhotoElem(c, i + 1);
                            }
                        } catch (error) {
                            console.error('Error fetching feature info:', error);
                        }
                    }
                    break; // exit for loop
                }
            }
        }
        return undefined;
    }

    // Posa o treu l'element del menú
    @action setAnchorEl(anchor: HTMLButtonElement) {
        this.anchorEl = anchor;
    }
}